import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { Popover } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import NoData from '../NoData';
import './index.scss';

// 比较两个数组是否相等
const valuesAreEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.every((val, index) => val === b[index]);
};

const AreaParentItem = memo(({ item, index, onClick, getCheckedChildrenCount }) => {
  return (
    <li
      key={item.value || index}
      className={`area-dropdown-item ${item?.checked ? 'selected' : ''}`}
      onClick={() => onClick(index)}
    >
      {item?.checked && getCheckedChildrenCount(index) === 0
        ? item?.name + '(全)'
        : getCheckedChildrenCount(index) > 0
          ? item?.name + '(' + getCheckedChildrenCount(index) + ')'
          : item?.name
      }
    </li>
  );
});

const AreaChildItem = memo(({ item, index, onClick }) => {
  return (
    <li
      key={item.value || index}
      className={`area-dropdown-item ${item?.checked ? 'selected' : ''}`}
      onClick={() => onClick(index)}
    >
      {item?.name}
    </li>
  );
});

const AddressSelector = ({
  options = [],
  onChange = () => { },
  labelName = 'name',
  valueName = 'code',
  customLabel = '工作区域',
  value = [],
  defaultValue = [],
}) => {
  const [selectedValues, setSelectedValues] = useState(defaultValue);
  const [localOptions, setLocalOptions] = useState(options);
  const [parentIndex, setParentIndex] = useState(undefined);
  const ref = useRef(null);
  const location = JSON.parse(sessionStorage.getItem("_user_location_")) || { value: "100000" };


  useEffect(() => {
    if (value && value.length > 0 && !valuesAreEqual(value, selectedValues)) {
      setSelectedValues(value);
    }
  }, [value, selectedValues]);

  useEffect(() => {
    setLocalOptions(options);
  }, [options]);

  const collectSelectedCodes = useCallback((data) => {
    return data.flatMap(parent => {
      if (!parent.checked) return [];

      const selectedChildren = parent.children?.filter(child => child.checked) || [];
      return selectedChildren.length === 0
        ? [parent.code || parent[valueName]]
        : selectedChildren.map(child => child.code || child[valueName]);
    });
  }, []);

  const parentChecked = useCallback((index) => {
    const newData = [...localOptions];
    const currentParentChecked = newData[index]?.checked || false;

    if (currentParentChecked && index === parentIndex) {
      newData[index] = {
        ...newData[index],
        checked: false,
        children: newData[index]?.children?.map(child => ({
          ...child,
          checked: false
        })) || []
      };
    } else if (currentParentChecked && index !== parentIndex) {
      // 无需修改数据
    } else {
      newData[index] = {
        ...newData[index],
        checked: true,
        children: newData[index]?.children?.map(child => ({
          ...child,
          checked: child?.checked
        })) || []
      };
    }

    setParentIndex(index);
    setLocalOptions(newData);

    const values = collectSelectedCodes(newData);
    if (!valuesAreEqual(values, selectedValues)) {
      setSelectedValues(values);
      onChange(values);
    }
  }, [collectSelectedCodes, selectedValues, localOptions]);

  const childChecked = useCallback((childIndex) => {
    if (parentIndex === undefined) return;

    const newData = [...localOptions];
    newData[parentIndex] = {
      ...newData[parentIndex],
      children: newData[parentIndex]?.children?.map((child, index) => {
        if (index !== childIndex) return child;
        return {
          ...child,
          checked: !child.checked
        };
      }) || []
    };

    setLocalOptions(newData);

    const values = collectSelectedCodes(newData);
    if (!valuesAreEqual(values, selectedValues)) {
      setSelectedValues(values);
      onChange(values);
    }
  }, [collectSelectedCodes, selectedValues, localOptions, parentIndex]);

  const getCheckedChildrenCount = useCallback((parentIndex) => {
    if (parentIndex === undefined) return 0;
    const children = localOptions[parentIndex]?.children || [];
    return children.filter(child => child.checked).length;
  }, [localOptions]);

  const resetAll = useCallback(() => {
    setLocalOptions(
      options.map(parent => ({
        ...parent,
        checked: false,
        children: parent.children?.map(child => ({
          ...child,
          checked: false
        })) || []
      }))
    );
    setParentIndex(undefined);

    const defaultLocationValue = location && location.value ? location?.value : "100000";
    const newValues = [defaultLocationValue];
    if (!valuesAreEqual(newValues, selectedValues)) {
      setSelectedValues(newValues);
      onChange(newValues);
    }
  }, [options, location, selectedValues]);

  const renderBox = useCallback(() => {
    const checkedData = localOptions.filter(item => item?.checked);
    return (
      <div className='address_selector_popup'>
        {localOptions && localOptions.length > 0 ? (
          <div className='city-area-select'>
            <ul className='dropdown-area-list'>
              {localOptions.map((item, index) => (
                <AreaParentItem
                  key={item.value || index}
                  item={item}
                  index={index}
                  onClick={parentChecked}
                  getCheckedChildrenCount={getCheckedChildrenCount}
                />
              ))}
            </ul>

            {parentIndex !== undefined &&
              localOptions[parentIndex]?.children &&
              localOptions[parentIndex]?.children.length > 0 ? (
              <div className='area-select-container'>
                <ul className='dropdown-area-list'>
                  {localOptions[parentIndex]?.children.map((item, index) => (
                    <AreaChildItem
                      key={item.value || index}
                      item={item}
                      index={index}
                      onClick={childChecked}
                    />
                  ))}
                </ul>
              </div>
            ) : null}

            {checkedData && checkedData.length > 0 ? (
              <div className='selected-area-section'>
                <ul>
                  {checkedData.map((item) => (
                    <li key={item.value}>
                      <span className='selected-area-name'>{item?.name}：</span>
                      <p className='selected-area-list'>
                        {item?.children?.filter(child => child.checked)?.length > 0
                          ? item?.children.filter(child => child.checked).map((i) => (
                            <a key={i.value}>{i.name}</a>
                          ))
                          : <a>全{item?.name}</a>
                        }
                      </p>
                    </li>
                  ))}
                </ul>
                <a className='clear-area-btn' onClick={resetAll}>清空已选位置</a>
              </div>
            ) : null}
          </div>
        ) : (
          <NoData />
        )}
      </div>
    );
  }, [localOptions, parentIndex, getCheckedChildrenCount, resetAll]);

  return (
    <div ref={ref} className={`address_selector `}>
      <Popover
        content={renderBox()}
        trigger="click"
        placement="bottomLeft"
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
      >
        <div className='select-box'>
          {value && value.length > 0 ? (
            <div className='selected'>{customLabel + '(' + value.length + ')'} </div>
          ) : (
            customLabel
          )}
          <span className={value && value.length > 0 ? 'select-arrow selected' : 'select-arrow'}>
            <CaretDownOutlined />
          </span>
        </div>
      </Popover>
    </div>
  );
};

export default AddressSelector;